import {
  computed,
  Directive,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
} from '@angular/core';
import { SW_SWIPE_CARD_DEFAULT_OPTIONS } from './swipe-card.config';

/** Subset of the HammerJS pan event this directive relies on. */
interface PanEvent {
  deltaX: number;
  deltaY: number;
  distance: number;
}

export interface SwipeResult<T> {
  like: boolean;
  data: T | null;
}

const FALLBACK_RELEASE_WIDTH = 400;

/**
 * `T` is the payload type forwarded from {@link simulateSwipe} to the `swiped` output.
 * It defaults to `any` because the directive cannot infer it from the template.
 */
@Directive({
  selector: '[recrewtSwipeable]',
  host: {
    '[style.width.px]': 'width()',
    '[style.height.px]': 'height()',
    '[class.swipe-card-heap]': 'true',
    '(pan)': 'onPan($event)',
    '(panend)': 'onPanEnd($event)',
  },
})
export class SwipeableDirective<T = any> implements OnInit, OnDestroy {
  private defaultConfig = inject(SW_SWIPE_CARD_DEFAULT_OPTIONS);

  protected el = inject(ElementRef);

  renderer = inject(Renderer2);

  public readonly width = input<number>();

  public readonly height = input<number>();

  public readonly fixed = input<boolean>(false);

  public readonly orientation = input('xy');

  public readonly released = output();

  public readonly swiped = output<SwipeResult<T>>();

  private readonly animationDuration = 200;

  private isReleased = false;

  private readonly element: HTMLElement = this.el.nativeElement;

  private overlay: HTMLElement | null = null;

  private readonly releaseRadius = computed(() => ({
    x: (this.width() ?? FALLBACK_RELEASE_WIDTH) / 4,
    y: (this.height() ?? 0) / 4,
  }));

  private timeoutId?: number;

  get allowSwipe(): boolean {
    return !this.fixed() && !this.isReleased;
  }

  onPan(event: PanEvent) {
    if (this.allowSwipe) {
      this.handleSwipeChange(event);
    }
  }

  onPanEnd(event: PanEvent) {
    if (this.allowSwipe) {
      const conditionX =
        this.orientation().includes('x') && this.releaseRadius().x < Math.abs(event.deltaX || 0);
      const conditionY =
        this.orientation().includes('y') && this.releaseRadius().y < Math.abs(event.deltaY || 0);
      const like = this.isSwipePositive(event);

      if (conditionX || conditionY) {
        this.handleSwipeEnd(like, null);
      } else {
        this.handleCancelSwipe();
      }
    }
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.timeoutId);
  }

  ngOnInit(): void {
    this.initializeOverlay();
  }

  public simulateSwipe(like: boolean, data: T | null): void {
    this.handleSwipeEnd(like, data);
  }

  private initializeOverlay(): void {
    this.overlay = document.createElement('div');
    this.renderer.appendChild(this.element, this.overlay);
  }

  private translate(options: { x: number; y: number; rotate?: number; duration?: number }) {
    const { x, y, rotate = 0, duration = 0 } = options;
    const positionX = x && this.orientation().includes('x') ? x : 0;
    const positionY = y && this.orientation().includes('y') ? y : 0;

    this.renderer.setStyle(this.element, 'transition', `transform ${duration}ms ease`);
    this.renderer.setStyle(
      this.element,
      'webkitTransform',
      `translate3d(${positionX}px, ${positionY}px, 0) rotate(${rotate}deg)`,
    );
  }

  private handleSwipeChange(event: PanEvent) {
    const like = this.isSwipePositive(event);
    const opacity = (Math.abs(event.distance) * 0.5) / this.element.offsetWidth;

    this.setBackgroundOverlay(like, opacity);
    this.translate({
      x: event.deltaX,
      y: event.deltaY,
      rotate: (event.deltaX * 20) / this.element.clientWidth,
    });
  }

  private handleCancelSwipe() {
    this.resetBackgroundOverlay();
    this.translate({
      x: 0,
      y: 0,
      rotate: 0,
      duration: this.animationDuration,
    });
  }

  private destroy() {
    this.resetElementStyles();
    this.resetBackgroundOverlay();
    this.renderer.removeChild(this.element, this.overlay);
    this.overlay = null;
  }

  private setBackgroundOverlay(like: boolean, opacity: number) {
    if (!this.overlay) return;
    const color = like ? this.defaultConfig.likeColor : this.defaultConfig.dislikeColor;
    this.overlay.className = like ? 'swipe-card-overlay-right' : 'swipe-card-overlay-left';
    this.renderer.setStyle(this.overlay, 'transition', 'opacity 0s ease');
    this.renderer.setStyle(
      this.overlay,
      'background-color',
      `rgba(${color}, ${opacity.toString()})`,
    );
  }

  private resetBackgroundOverlay(): void {
    if (!this.overlay) return;
    this.renderer.setStyle(this.overlay, 'transition', `opacity ${this.animationDuration}ms ease`);
    this.renderer.setStyle(this.overlay, 'background-color', 'rgba(255,255,255,0)');
  }

  private resetElementStyles(): void {
    this.renderer.setStyle(this.element, 'transition', 'transform 0s ease');
    this.renderer.setStyle(this.element, 'webkitTransform', 'translate3d(0, 0, 0) rotate(0deg)');
  }

  private isSwipePositive(event: PanEvent): boolean {
    const orientation = this.orientation();
    return (orientation === 'y' && event.deltaY < 0) || (orientation !== 'y' && event.deltaX > 0);
  }

  /** Animates the card off-screen in the swipe direction, tinting it with the verdict color. */
  private animateCardOffscreen(like: boolean): void {
    const { offsetHeight, offsetWidth, clientHeight, clientWidth } = this.element;
    const y = (offsetHeight + clientHeight) * (like ? -1 : 1);
    const x = (offsetWidth + clientWidth) * (like ? 1 : -1);

    this.setBackgroundOverlay(like, 0.35);
    this.translate({
      x,
      y,
      rotate: (x * 20) / clientWidth,
      duration: 800,
    });
  }

  private handleSwipeEnd(like: boolean, data: T | null): void {
    this.animateCardOffscreen(like);
    this.isReleased = true;

    this.timeoutId = window.setTimeout(() => {
      this.swiped.emit({ like, data });
      this.released.emit();
      this.destroy();
    }, this.animationDuration);
  }
}
